import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { RootState } from '../store';
import { Trash2 } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useAdminLogBulkDelete } from '../hooks/useAdminLogBulkDelete';
import { DateInput } from '../components/DateInput';

interface AuditLog {
  id: string;
  shopId: string;
  entityType: 'APPOINTMENT' | 'TIME_OFF' | 'CLOSURE' | 'SCHEDULE_RULE' | 'SYNC_ACCOUNT' | 'CLIENT';
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'CONFIRM' | 'CANCEL' | 'RESCHEDULE' | 'SYNC';
  userId: string;
  userRole: string;
  userDisplayName: string;
  changes: any;
  metadata: any;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

interface AuditFilters {
  shopId: string;
  entityType: string;
  action: string;
  userId: string;
  dateFrom: string;
  dateTo: string;
  limit: number;
  offset: number;
}

interface AuditStats {
  stats: Array<{
    entityType: string;
    action: string;
    count: number;
    uniqueUsers: number;
    firstAction: string;
    lastAction: string;
  }>;
  totals: {
    totalActions: number;
    totalUsers: number;
    totalShops: number;
    totalEntities: number;
  };
  byUser?: Array<{
    userId: string;
    userRole?: string;
    displayName: string;
    count: number;
  }>;
}

export function AuditLogsPage() {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = user?.role === 'super_admin';
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [shops, setShops] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

  const [filters, setFilters] = useState<AuditFilters>({
    shopId: '',
    entityType: '',
    action: '',
    userId: '',
    dateFrom: '',
    dateTo: '',
    limit: 50,
    offset: 0,
  });

  const entityTypes = [
    { value: 'APPOINTMENT', label: t('planning.audit.entityTypes.appointment') },
    { value: 'CLIENT', label: t('planning.audit.entityTypes.client') },
    { value: 'TIME_OFF', label: t('planning.audit.entityTypes.timeOff') },
    { value: 'CLOSURE', label: t('planning.audit.entityTypes.closure') },
    { value: 'SCHEDULE_RULE', label: t('planning.audit.entityTypes.scheduleRule') },
    { value: 'SYNC_ACCOUNT', label: t('planning.audit.entityTypes.syncAccount') },
  ];

  const actions = [
    { value: 'CREATE', label: t('planning.audit.actions.create') },
    { value: 'UPDATE', label: t('planning.audit.actions.update') },
    { value: 'DELETE', label: t('planning.audit.actions.delete') },
    { value: 'CONFIRM', label: t('planning.audit.actions.confirm') },
    { value: 'CANCEL', label: t('planning.audit.actions.cancel') },
    { value: 'RESCHEDULE', label: t('planning.audit.actions.reschedule') },
    { value: 'SYNC', label: t('planning.audit.actions.sync') },
  ];

  const loadAuditLogs = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'shopId') queryParams.append(key, value.toString());
      });
      if (isSuperAdmin) {
        if (filters.shopId) {
          queryParams.append('shopId', filters.shopId);
        } else {
          queryParams.append('allShops', 'true');
        }
      }

      const response = await apiClient(`/api/planning/audit?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setAuditLogs(data.data.auditLogs || []);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, canLoad, isSuperAdmin]);

  const loadAuditStats = useCallback(async () => {
    if (!canLoad) return;
    try {
      const queryParams = new URLSearchParams();
      if (isSuperAdmin) {
        if (filters.shopId) queryParams.append('shopId', filters.shopId);
        else queryParams.append('allShops', 'true');
      }
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

      const response = await apiClient(`/api/planning/audit/stats?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setAuditStats(data.data);
      }
    } catch (error) {
      console.error('Failed to load audit stats:', error);
    }
  }, [filters.shopId, filters.dateFrom, filters.dateTo, canLoad, isSuperAdmin]);

  useEffect(() => {
    if (!canLoad) return;
    loadAuditLogs();
    loadAuditStats();
    loadShops();
  }, [loadAuditLogs, loadAuditStats, canLoad, currentShopId]);

  const loadShops = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await apiClient('/api/admin/shops?limit=1000');

      if (!response.ok) {
        throw new Error('Failed to fetch shops');
      }

      const data = await response.json();
      if (data.success) {
        setShops(data.data.shops || []);
      }
    } catch (error) {
      console.error('Failed to load shops:', error);
    }
  };

  const visibleIds = useMemo(
    () => auditLogs.map((log) => log.id).filter((id): id is string => typeof id === 'string' && id.length > 0),
    [auditLogs],
  );
  const {
    selectedArray,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const bulkDelete = useAdminLogBulkDelete({
    softDeletePath: '/api/admin/planning-audit/bulk-soft-delete',
    hardDeletePath: '/api/admin/planning-audit/bulk-hard-delete',
    i18n: {
      error: 'planning.audit.bulkDelete.error',
      partialSoft: 'planning.audit.bulkDelete.partialSoft',
      partialHard: 'planning.audit.bulkDelete.partialHard',
      softSuccess: 'planning.audit.bulkDelete.softSuccess',
      hardSuccess: 'planning.audit.bulkDelete.hardSuccess',
      softTitle: 'planning.audit.bulkDelete.softTitle',
      softMessage: 'planning.audit.bulkDelete.softMessage',
      entityLabel: 'planning.audit.bulkDelete.entityLabel',
    },
    onComplete: async () => {
      clearSelection();
      await loadAuditLogs();
      await loadAuditStats();
    },
  });

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionBadge = (action: string) => {
    const colors: { [key: string]: string } = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
      CONFIRM: 'bg-green-100 text-green-800',
      CANCEL: 'bg-yellow-100 text-yellow-800',
      RESCHEDULE: 'bg-purple-100 text-purple-800',
      SYNC: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[action] || 'bg-gray-100 text-gray-800'}`}>
        {action}
      </span>
    );
  };

  const getEntityTypeBadge = (entityType: string) => {
    const colors: { [key: string]: string } = {
      APPOINTMENT: 'bg-blue-100 text-blue-800',
      CLIENT: 'bg-teal-100 text-teal-800',
      TIME_OFF: 'bg-yellow-100 text-yellow-800',
      CLOSURE: 'bg-red-100 text-red-800',
      SCHEDULE_RULE: 'bg-purple-100 text-purple-800',
      SYNC_ACCOUNT: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[entityType] || 'bg-gray-100 text-gray-800'}`}>
        {entityType}
      </span>
    );
  };

  const getAuditActorName = (log: AuditLog) =>
    log.metadata?.actorDisplayName || log.userDisplayName || log.userId || '';

  const exportAuditLogs = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Role', 'Entity Type', 'Action', 'Entity ID', 'IP Address', 'User Agent'].join(','),
      ...auditLogs.map((log) =>
        [
          formatDateTime(log.timestamp),
          getAuditActorName(log),
          log.userRole,
          log.entityType,
          log.action,
          log.entityId,
          log.ipAddress,
          `"${log.userAgent}"`,
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="space-y-6">
      {bulkDelete.toast && (
        <div
          className={`fixed top-20 right-4 z-50 max-w-md rounded-lg border px-4 py-3 shadow-lg text-sm ${
            bulkDelete.toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : bulkDelete.toast.type === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {bulkDelete.toast.message}
        </div>
      )}

      <div className="dt-list-toolbar flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('planning.audit.title')}</h1>
          <p className="text-gray-600">{t('planning.audit.subtitle')}</p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('planning.audit.filtersLabel')}
          </button>
          <button
            onClick={exportAuditLogs}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {t('planning.audit.export')}
          </button>
        </div>
      </div>

      {auditStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm font-semibold">📊</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('planning.audit.stats.totalActions')}</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.totals.totalActions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-sm font-semibold">👥</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('planning.audit.stats.totalUsers')}</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.totals.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 text-sm font-semibold">🏪</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('planning.audit.stats.totalShops')}</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.totals.totalShops}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 text-sm font-semibold">📝</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('planning.audit.stats.totalEntities')}</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.totals.totalEntities}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {auditStats?.byUser && auditStats.byUser.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 mb-3">{t('planning.audit.stats.byUser')}</h3>
          <div className="flex flex-wrap gap-3">
            {auditStats.byUser.slice(0, 8).map((actor) => (
              <button
                key={actor.userId}
                type="button"
                onClick={() => setFilters({ ...filters, userId: actor.userId, offset: 0 })}
                className={`px-3 py-2 rounded-md text-sm border ${
                  filters.userId === actor.userId
                    ? 'border-blue-300 bg-blue-50 text-blue-800'
                    : 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100'
                }`}
              >
                <span className="font-medium">{actor.displayName}</span>
                <span className="ml-2 text-gray-500">{actor.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="dt-filters grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {isSuperAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.shop')}</label>
              <select
                value={filters.shopId}
                onChange={(e) => setFilters({ ...filters, shopId: e.target.value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('planning.audit.filters.allShops')}</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.entityType')}</label>
              <select
                value={filters.entityType}
                onChange={(e) => setFilters({ ...filters, entityType: e.target.value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('planning.audit.filters.allTypes')}</option>
                {entityTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.action')}</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('planning.audit.filters.allActions')}</option>
                {actions.map((action) => (
                  <option key={action.value} value={action.value}>
                    {action.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.user')}</label>
              <select
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('planning.audit.filters.allUsers')}</option>
                {(auditStats?.byUser || []).map((actor) => (
                  <option key={actor.userId} value={actor.userId}>
                    {actor.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.dateFrom')}</label>
              <DateInput
                
                value={filters.dateFrom}
                onChange={(value) => setFilters({ ...filters, dateFrom: value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.dateTo')}</label>
              <DateInput
                value={filters.dateTo}
                onChange={(value) => setFilters({ ...filters, dateTo: value, offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.filters.limit')}</label>
              <select
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value, 10), offset: 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900">
              {t('planning.audit.logs')} ({auditLogs.length})
            </h3>
            {selectedCount > 0 && (
              <span className="text-sm text-primary-600 font-medium">
                {t('planning.audit.bulkDelete.selected', { count: selectedCount })}
              </span>
            )}
          </div>
          {selectedCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setPendingDeleteIds([...selectedArray]);
                  bulkDelete.setSoftDeleteModalOpen(true);
                }}
                disabled={bulkDelete.deleteLoading}
                className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('planning.audit.bulkDelete.soft', { count: selectedCount })}
              </button>
              {bulkDelete.canHardDelete && (
                <button
                  type="button"
                  onClick={() => {
                    setPendingDeleteIds([...selectedArray]);
                    bulkDelete.setHardDeleteModalOpen(true);
                  }}
                  disabled={bulkDelete.deleteLoading}
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('planning.audit.bulkDelete.hard', { count: selectedCount })}
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="p-12 text-center text-gray-500">{t('planning.audit.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={auditLogs.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('planning.audit.bulkDelete.selectAll')}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.timestamp')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.entityType')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.action')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.entityId')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.audit.table.details')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr
                    key={log.id}
                    className={`hover:bg-gray-50 ${selectedArray.includes(log.id) ? 'bg-primary-50/40' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedArray.includes(log.id)}
                        onChange={() => toggleSelect(log.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        aria-label={t('planning.audit.bulkDelete.selectRow')}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{getAuditActorName(log)}</div>
                      <div className="text-sm text-gray-500">{log.userRole}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getEntityTypeBadge(log.entityType)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getActionBadge(log.action)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.entityId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button onClick={() => setSelectedLog(log)} className="text-blue-600 hover:text-blue-900">
                        {t('planning.audit.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={bulkDelete.softDeleteModalOpen}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setSoftDeleteModalOpen(false)}
        onConfirm={() => bulkDelete.confirmBulkSoftDelete(pendingDeleteIds)}
        title={t('planning.audit.bulkDelete.softTitle')}
        message={t('planning.audit.bulkDelete.softMessage', { count: pendingDeleteIds.length || selectedCount })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="warning"
        isLoading={bulkDelete.deleteLoading}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkDelete.hardDeleteModalOpen}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setHardDeleteModalOpen(false)}
        onConfirm={() => bulkDelete.confirmBulkHardDelete(pendingDeleteIds)}
        entityLabel={t('planning.audit.bulkDelete.entityLabel')}
        count={pendingDeleteIds.length || selectedCount}
        isLoading={bulkDelete.deleteLoading}
      />

      {selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('planning.audit.details.title')}</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.timestamp')}</label>
                    <p className="text-sm text-gray-900">{formatDateTime(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.user')}</label>
                    <p className="text-sm text-gray-900">
                      {getAuditActorName(selectedLog)} ({selectedLog.userRole})
                    </p>
                    {selectedLog.metadata?.sessionUserDisplayName && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('planning.audit.details.sessionUser')}: {selectedLog.metadata.sessionUserDisplayName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.entityType')}</label>
                    <p className="text-sm text-gray-900">{selectedLog.entityType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.action')}</label>
                    <p className="text-sm text-gray-900">{selectedLog.action}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.entityId')}</label>
                  <p className="text-sm text-gray-900 font-mono">{selectedLog.entityId}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.details.changes')}</label>
                  <pre className="bg-gray-100 p-3 rounded-md text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.changes, null, 2)}
                  </pre>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('planning.audit.details.metadata')}</label>
                  <pre className="bg-gray-100 p-3 rounded-md text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.ipAddress')}</label>
                    <p className="text-sm text-gray-900 font-mono">{selectedLog.ipAddress}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('planning.audit.details.userAgent')}</label>
                    <p className="text-sm text-gray-900 truncate" title={selectedLog.userAgent}>
                      {selectedLog.userAgent}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button onClick={() => setSelectedLog(null)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
